import { LoaderFunctionArgs, useLoaderData, useParams } from "react-router-dom";
import { QueryClient, useQuery } from "@tanstack/react-query";
import BookingDetailsItem from "@/components/bookings/details/BookingDetailsItem";
import { ApiService } from "@/api/services/ApiService";
import {
  getFormattedTime,
  getFormattedTimeAndDate,
  getShortDate,
  getStatusText,
} from "@/lib/helpers";
import {
  IWorkOrder,
  IWorkOrderContact,
  IWorkOrderOtherTech,
  IWorkOrderOwnerIncident,
} from "@/lib/types";

// ---------------------- API

const workOrderQuery = (bookingId: string) => ({
  queryKey: ["work-order-info", bookingId] as const,
  queryFn: async () => ApiService.bookings.getWorkOrder(bookingId),
});

const workOrderContactQuery = (contactId: string) => ({
  queryKey: ["work-order-contact", contactId] as const,
  queryFn: async () => ApiService.bookings.getWorkOrderContact(contactId),
  enabled: !!contactId,
});

const workOrderOwnerQuery = (incidentId: string) => ({
  queryKey: ["work-order-owner", incidentId] as const,
  queryFn: async () => ApiService.bookings.getWorkOrderOwnerByIncident(incidentId),
  enabled: !!incidentId,
});

const workOrderOtherTechsQuery = (workOrderName: string, resourceId: string, date: string) => ({
  queryKey: ["work-order-other-techs", `${workOrderName}-${resourceId}-${date}`] as const,
  queryFn: async () => ApiService.bookings.getWorkOrderOtherTechs(workOrderName, resourceId, date),
  enabled: !!workOrderName && !!resourceId && !!date,
});

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const bookingId = params.id;
    if (!bookingId) throw new Error("Booking ID not found");

    try {
      const workOrder = await queryClient.ensureQueryData(workOrderQuery(bookingId));

      // Only fetch site contact if we have an ID
      const siteContact = workOrder.siteContact
        ? await queryClient.ensureQueryData(workOrderContactQuery(workOrder.siteContact))
        : null;

      // Only fetch owner if we have an ID
      const owner = workOrder.serviceRequestId
        ? await queryClient.ensureQueryData(workOrderOwnerQuery(workOrder.serviceRequestId))
        : null;

      // Only fetch other techs if we have all the info we need
      const otherTechs =
        workOrder.workOrderName && workOrder.resource && workOrder.startTime
          ? await queryClient.ensureQueryData(
              workOrderOtherTechsQuery(
                workOrder.workOrderName ?? "",
                workOrder.resource ?? "",
                workOrder.startTime ? getShortDate(workOrder.startTime) : "",
              ),
            )
          : null;

      return { workOrder, siteContact, owner, otherTechs };
    } catch (error) {
      throw error; // Let React Router handle the error
    }
  };

// ---------------------- Component

const BookingOverview = () => {
  // --------- Variables / Hooks

  const { id = "" } = useParams();

  const initialData = useLoaderData() as {
    workOrder: IWorkOrder;
    siteContact: IWorkOrderContact;
    owner: IWorkOrderOwnerIncident;
    otherTechs: IWorkOrderOtherTech[];
  };

  const { data: workOrder } = useQuery({
    ...workOrderQuery(id),
    initialData: initialData.workOrder,
  });

  const { data: siteContact } = useQuery({
    ...workOrderContactQuery(workOrder.siteContact ?? ""),
    initialData: initialData.siteContact,
  });

  const { data: owner } = useQuery({
    ...workOrderOwnerQuery(workOrder.serviceRequestId ?? ""),
    initialData: initialData.owner,
  });

  const { data: otherTechs } = useQuery({
    ...workOrderOtherTechsQuery(
      workOrder.workOrderName ?? "",
      workOrder.resource ?? "",
      workOrder.startTime ? getShortDate(workOrder.startTime) : "",
    ),
    initialData: initialData.otherTechs,
  });

  // --------- JSX

  return (
    <div className="mb-1 flex h-auto w-full flex-grow flex-col gap-3">
      {/*******************************************/}
      {/* Work Order */}
      {/*******************************************/}

      <BookingDetailsItem title="Work Order">
        <a
          href={`https://cenero.cenero.com/main.aspx?appid=8233cdc8-388b-eb11-a982-005056ad0c7f&pagetype=entityrecord&etn=msdyn_workorder&id=${workOrder.workOrderId}`}
          target="_blank"
          className="underline"
        >
          {workOrder.workOrderName}
        </a>
      </BookingDetailsItem>

      {/*******************************************/}
      {/* Account */}
      {/*******************************************/}

      <BookingDetailsItem title="Account">{workOrder.serviceAccountName}</BookingDetailsItem>

      {/*******************************************/}
      {/* Address */}
      {/*******************************************/}

      <BookingDetailsItem title="Address">
        {
          <>
            <p>{workOrder.address1}</p>
            <p>{`${workOrder.city}, ${workOrder.stateOrProvince} ${workOrder.postalCode}`}</p>
          </>
        }
      </BookingDetailsItem>

      {/*******************************************/}
      {/* Start Time */}
      {/*******************************************/}

      <BookingDetailsItem title="Start Time">
        {getFormattedTimeAndDate(workOrder.localStartTime)}
      </BookingDetailsItem>

      {/*******************************************/}
      {/* End Time */}
      {/*******************************************/}

      <BookingDetailsItem title="End Time">
        {getFormattedTimeAndDate(workOrder.localEndTime)}
      </BookingDetailsItem>

      {/*******************************************/}
      {/* Type */}
      {/*******************************************/}

      <BookingDetailsItem title="Type">{workOrder.workOrderTypeName}</BookingDetailsItem>

      {/*******************************************/}
      {/* Booking Status */}
      {/*******************************************/}

      <BookingDetailsItem title="Booking Status">
        {getStatusText(workOrder.bookingStatus)}
      </BookingDetailsItem>

      {/*******************************************/}
      {/* Service Call */}
      {/*******************************************/}

      <BookingDetailsItem title="Service Call">{workOrder.serviceRequestName}</BookingDetailsItem>

      {/*******************************************/}
      {/* Ticket Number */}
      {/*******************************************/}

      <BookingDetailsItem title="Ticket Number">
        {owner?.ticketNumber ? (
          <p>{owner?.ticketNumber}</p>
        ) : (
          <p className="mb-4">Unable to retrieve this information.</p>
        )}
      </BookingDetailsItem>

      {/*******************************************/}
      {/* TSE */}
      {/*******************************************/}

      <BookingDetailsItem title="TSE">
        {owner?.ownerName ? (
          <p>{owner?.ownerName}</p>
        ) : (
          <p className="mb-4">Unable to retrieve this information.</p>
        )}
      </BookingDetailsItem>

      {/*******************************************/}
      {/* Site Contact */}
      {/*******************************************/}

      <BookingDetailsItem title="Site Contact">
        {siteContact?.fullName || siteContact?.email ? (
          <>
            <p>{siteContact?.fullName}</p>
            <p>{siteContact?.email}</p>
            <p>{siteContact?.telephone}</p>
            <p>{siteContact?.mobilePhone}</p>
          </>
        ) : (
          <p className="mb-4">Unable to retrieve this information.</p>
        )}
      </BookingDetailsItem>

      {/*******************************************/}
      {/* Other Techs */}
      {/*******************************************/}

      {otherTechs.length > 0 && (
        <BookingDetailsItem title="Other Techs Onsite Today">
          {otherTechs.map((tech, idx) => (
            <div
              key={idx}
              className="mb-3"
            >
              <p>{tech.resourceName}</p>
              <p>Start Time: {getFormattedTime(tech.localStartTime)}</p>
              <p>End Time: {getFormattedTime(tech.localEndTime)}</p>
            </div>
          ))}
        </BookingDetailsItem>
      )}
    </div>
  );
};

export default BookingOverview;
